import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../services/api';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => useContext(SiteSettingsContext);

// Default fallbacks — used ONLY when the settings API call fails
const defaultNavLinks = [
  { key: 'services', path: '/services', label: 'Services', visible: true },
  { key: 'industries', path: '/industries', label: 'Industries', visible: true },
  { key: 'portfolio', path: '/portfolio', label: 'Portfolio', visible: true },
  { key: 'blog', path: '/blog', label: 'Blog', visible: true },
  { key: 'case-studies', path: '/case-studies', label: 'Case Studies', visible: true },
  { key: 'about', path: '/about', label: 'About', visible: true },
  { key: 'careers', path: '/careers', label: 'Careers', visible: true },
  { key: 'contact', path: '/contact', label: 'Contact', visible: true },
];

const defaultCtaButtons = [
  { key: 'schedule-meet', label: 'Schedule Meet', path: '/contact?type=call', visible: true, style: 'outline' },
  { key: 'get-a-quote', label: 'Get a Quote', path: '/contact', visible: true, style: 'filled' },
];

const defaultFooterLinks = [
  { key: 'home', label: 'Home', path: '/', visible: true },
  { key: 'services', label: 'Services', path: '/services', visible: true },
  { key: 'portfolio', label: 'Portfolio', path: '/portfolio', visible: true },
  { key: 'blog', label: 'Blog', path: '/blog', visible: true },
  { key: 'about', label: 'About Us', path: '/about', visible: true },
  { key: 'contact', label: 'Contact', path: '/contact', visible: true },
];

export const SiteSettingsProvider = ({ children }) => {
  const [allNavLinks, setAllNavLinks] = useState([]);
  const [allCtaButtons, setAllCtaButtons] = useState([]);
  const [allFooterLinks, setAllFooterLinks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.getSettings();
        const data = res.data.data;
        setAllNavLinks(data.navLinks || defaultNavLinks);
        setAllCtaButtons(data.ctaButtons || defaultCtaButtons);
        setAllFooterLinks(data.footerLinks || defaultFooterLinks);
      } catch (error) {
        console.warn('Failed to load site settings, using defaults:', error);
        setAllNavLinks(defaultNavLinks);
        setAllCtaButtons(defaultCtaButtons);
        setAllFooterLinks(defaultFooterLinks);
      } finally {
        // Always set loaded to true, even on error
        setLoaded(true);
      }
    };
    
    fetchSettings();
  }, []);

  // Visible-only lists
  const visibleNavLinks = allNavLinks.filter((l) => l.visible);
  const visibleCtaButtons = allCtaButtons.filter((b) => b.visible);
  const visibleFooterLinks = allFooterLinks.filter((l) => l.visible);

  // Map of hidden paths (for route blocking)
  const hiddenPaths = new Set(
    allNavLinks.filter((l) => !l.visible).map((l) => l.path)
  );

  // Refresh settings (e.g. after admin saves)
  const refreshSettings = async () => {
    try {
      const res = await api.getSettings();
      const data = res.data.data;
      setAllNavLinks(data.navLinks || defaultNavLinks);
      setAllCtaButtons(data.ctaButtons || defaultCtaButtons);
      setAllFooterLinks(data.footerLinks || defaultFooterLinks);
    } catch {
      // keep existing
    }
  };

  return (
    <SiteSettingsContext.Provider
      value={{
        loaded,
        allNavLinks,
        allCtaButtons,
        allFooterLinks,
        visibleNavLinks,
        visibleCtaButtons,
        visibleFooterLinks,
        hiddenPaths,
        refreshSettings,
      }}
    >
      {children}
    </SiteSettingsContext.Provider>
  );
};
